import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Loading, ResourceProps, useAuthProvider, useLogout, useNotify } from 'react-admin'
import isEqual from 'lodash/isEqual'

import config from '../../config'
import PermissionContext from './PermissionContext'
import { useUserContextProvider } from '../user-context'
import {
  AutomaticallyLogoutWithError,
  PermissionPayload,
  RegisterResourcePropsWithPermissionFn,
  ResetPermissionFn,
  UpdatePermissionFn,
} from './type'
import { extractPermisionSubmenu } from '../../utils/user'
import { transformMenu } from '../../utils/menu'
import { MenuPermission } from '../../utils/menu/type'

const defaultPermissions = {
  creatable: false,
  updatable: false,
  readable: false,
}

export const PermissionContextProvider = ({ children }): JSX.Element => {
  const doLogout = useLogout()
  const notify = useNotify()
  const {
    currentUserData,
    isLoading: isUserLoading,
    error: errorUser,
    resetUserData,
  } = useUserContextProvider()
  const { setIdentityUser } = useAuthProvider()
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentPermissions, setCurrentPermissions] = useState<PermissionPayload>(null)
  const [menuConfig, setMenuConfig] = useState<MenuPermission[]>([])

  const registerResourcePropsWithPermission: RegisterResourcePropsWithPermissionFn = useCallback(
    (resourceProps, permissions) => {
      const { name, list = null, create = null, edit = null, show = null, ...rest } = resourceProps
      const { creatable, updatable, readable } =
        permissions.find((x) => x.key === config(`resources.keys.${name}`)) || defaultPermissions

      const updatePropsWithPermission: ResourceProps = {
        ...rest,
        name,
        list: readable ? list : null,
        create: creatable ? create : null,
        edit: updatable ? edit : null,
        show: readable ? show : null,
      }
      return updatePropsWithPermission
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(currentPermissions)],
  )

  const updatePermission: UpdatePermissionFn = useCallback((newPermissions) => {
    setCurrentPermissions(newPermissions)
  }, [])

  const resetPermission: ResetPermissionFn = useCallback(() => {
    setCurrentPermissions(null)
    setError(null)
  }, [])

  const automaticallyLogoutWithError: AutomaticallyLogoutWithError = useCallback(
    (error = null) => {
      resetPermission()
      resetUserData()
      if (error && error.message) {
        notify(error.message, {
          type: 'warning',
        })
      }
      doLogout()
    },
    [doLogout, notify, resetPermission, resetUserData],
  )

  // useEffect:: check Error
  useEffect(() => {
    if (errorUser) {
      // if the user data was not found
      if (errorUser) {
        setError(errorUser)
      }

      // if the user permission was not found
      if (!currentPermissions) {
        setError(new Error('ra.auth.permission_not_found'))
      }
    }

    if (error) automaticallyLogoutWithError(error)
  }, [errorUser, error, automaticallyLogoutWithError, currentPermissions])

  // useEffect:: check and update permission state
  useEffect(() => {
    if (isUserLoading) {
      if (!isLoading) setIsLoading(true)
      return
    }
    const extractedPermissions = extractPermisionSubmenu(currentUserData?.role?.permissions)
    if (isEqual(currentPermissions, extractedPermissions)) return

    if (currentUserData?.role?.permissions) {
      setMenuConfig(transformMenu(currentUserData?.role?.permissions))
    }

    setIdentityUser(currentUserData)
    setCurrentPermissions(extractedPermissions)
    setIsLoading(false)
  }, [currentPermissions, currentUserData, isLoading, isUserLoading, setIdentityUser])

  const contextValue = useMemo(
    () => ({
      registerResourcePropsWithPermission,
      updatePermission,
      resetPermission,
      currentPermissions,
      menuConfig,
      isLoading,
      error,
    }),
    [
      registerResourcePropsWithPermission,
      updatePermission,
      resetPermission,
      currentPermissions,
      menuConfig,
      isLoading,
      error,
    ],
  )
  if (isLoading) return <Loading />
  return <PermissionContext.Provider value={contextValue}>{children}</PermissionContext.Provider>
}
